import { format } from 'date-fns'
import { UserSegment } from 'api/generated'
import type { UserSearchParams, UserSort } from 'types/users'

// Radix Select has no empty value, so "nessun filtro" needs a sentinel
export const ALL = 'all'

/** Tutti / Sì / No for the boolean filters. */
export type TriState = typeof ALL | 'yes' | 'no'

export interface FilterState {
  query: string
  premium: TriState
  fitp: TriState
  /** Confirmed account: email confirmed OR social login (Apple/Google/Facebook). */
  accountConfirmed: TriState
  banned: TriState
  createdFrom?: Date
  createdTo?: Date
  lastActiveFrom?: Date
  lastActiveTo?: Date
  segment: string
  platform: string
  loginMethod: string
  /** Platform of the last client seen (X-Client-Platform). */
  clientPlatform: string
  /** `platform|version` of a UserClientVersion, or ALL. */
  clientVersion: string
  /** Last client version seen older than this (free text, e.g. 3.6.0). */
  clientVersionBelow: string
  lastSignInSource: string
  sort: UserSort
  direction: 'asc' | 'desc'
}

// Newest users first, like the old GET /users listing
export const initialFilters: FilterState = {
  query: '',
  premium: ALL,
  fitp: ALL,
  accountConfirmed: ALL,
  banned: ALL,
  segment: ALL,
  platform: ALL,
  loginMethod: ALL,
  clientPlatform: ALL,
  clientVersion: ALL,
  clientVersionBelow: '',
  lastSignInSource: ALL,
  sort: 'id',
  direction: 'desc',
}

/** The Tutti/Sì/No filters shown in the bar, in order. */
export const QUICK_FILTERS = [
  { key: 'premium', label: 'Premium' },
  { key: 'fitp', label: 'FITP' },
  { key: 'accountConfirmed', label: 'Confermato' },
  { key: 'banned', label: 'Bannato' },
] as const

export const SEGMENT_LABELS: Record<UserSegment, string> = {
  [UserSegment.PRO]: 'Pro',
  [UserSegment.AGONISTA]: 'Agonista',
  [UserSegment.AMATORIALE]: 'Amatoriale',
  [UserSegment.FAN]: 'Fan',
}

export const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
}

/** Last client seen: the native app on iOS/Android or the website. */
export const CLIENT_PLATFORM_LABELS: Record<string, string> = {
  ios: 'App iOS',
  android: 'App Android',
  web: 'Web',
  not_tracked: 'Non tracciata',
}

export const SOURCE_LABELS: Record<string, string> = {
  app: 'App',
  web: 'Web',
  not_tracked: 'Non tracciato',
}

/** Value of the client version select: platform and version together. */
export const clientVersionKey = (platform: string | null, version: string) =>
  `${platform ?? ''}|${version}`

const parseClientVersion = (key: string) => {
  const [platform, version] = key.split('|')
  return { platform, version }
}

export const clientVersionLabel = (key: string) => {
  const { platform, version } = parseClientVersion(key)
  return `${platform ? (CLIENT_PLATFORM_LABELS[platform] ?? platform) + ' ' : ''}${version}`
}

export const LOGIN_METHOD_LABELS: Record<string, string> = {
  social: 'Social (Apple/Google/Facebook)',
  password: 'Email e password',
}

export const SORT_LABELS: Partial<Record<UserSort, string>> = {
  id: 'ID',
  created_at: 'Data iscrizione',
  last_active_at: 'Ultimo accesso',
  premium_expiration: 'Scadenza premium',
  name: 'Nome',
}

const triState = (value: TriState) =>
  value === ALL ? undefined : value === 'yes'

const day = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : undefined)

export const toParams = (f: FilterState): UserSearchParams => ({
  query: f.query.trim() || undefined,
  premium: triState(f.premium),
  has_fit_player_profile: triState(f.fitp),
  account_confirmed: triState(f.accountConfirmed),
  banned: triState(f.banned),
  created_from: day(f.createdFrom),
  created_to: day(f.createdTo),
  last_active_from: day(f.lastActiveFrom),
  last_active_to: day(f.lastActiveTo),
  segments: f.segment === ALL ? undefined : [f.segment as UserSegment],
  platforms:
    f.platform === ALL
      ? undefined
      : [f.platform as NonNullable<UserSearchParams['platforms']>[number]],
  login_method:
    f.loginMethod === ALL
      ? undefined
      : (f.loginMethod as UserSearchParams['login_method']),
  ...clientParams(f),
  last_sign_in_sources:
    f.lastSignInSource === ALL
      ? undefined
      : [
          f.lastSignInSource as NonNullable<
            UserSearchParams['last_sign_in_sources']
          >[number],
        ],
  client_version_below: f.clientVersionBelow.trim() || undefined,
  sort: f.sort,
  direction: f.direction,
})

type ClientPlatformParam = NonNullable<
  UserSearchParams['client_platforms']
>[number]

/** A picked version also pins its platform (3.6.4 on iOS ≠ 3.6.4 on web). */
const clientParams = (
  f: FilterState
): Pick<UserSearchParams, 'client_platforms' | 'client_versions'> => {
  if (f.clientVersion !== ALL) {
    const { platform, version } = parseClientVersion(f.clientVersion)
    return {
      client_versions: [version],
      client_platforms: platform
        ? [platform as ClientPlatformParam]
        : f.clientPlatform === ALL
          ? undefined
          : [f.clientPlatform as ClientPlatformParam],
    }
  }
  return {
    client_platforms:
      f.clientPlatform === ALL
        ? undefined
        : [f.clientPlatform as ClientPlatformParam],
  }
}

export interface FilterChip {
  /** The FilterState keys reset when the chip is removed. */
  keys: (keyof FilterState)[]
  label: string
}

const range = (label: string, from?: Date, to?: Date) =>
  `${label} ${from ? format(from, 'dd/MM/yyyy') : '…'} – ${to ? format(to, 'dd/MM/yyyy') : 'oggi'}`

/** The active filters, as removable chips. The text search stays in the bar. */
export const filterChips = (f: FilterState): FilterChip[] => {
  const chips: FilterChip[] = []
  for (const { key, label } of QUICK_FILTERS) {
    if (f[key] !== ALL)
      chips.push({
        keys: [key],
        label: `${label}: ${f[key] === 'yes' ? 'sì' : 'no'}`,
      })
  }
  if (f.createdFrom || f.createdTo)
    chips.push({
      keys: ['createdFrom', 'createdTo'],
      label: range('Iscritti', f.createdFrom, f.createdTo),
    })
  if (f.lastActiveFrom || f.lastActiveTo)
    chips.push({
      keys: ['lastActiveFrom', 'lastActiveTo'],
      label: range('Attivi', f.lastActiveFrom, f.lastActiveTo),
    })
  if (f.segment !== ALL)
    chips.push({
      keys: ['segment'],
      label: SEGMENT_LABELS[f.segment as UserSegment] ?? f.segment,
    })
  if (f.platform !== ALL)
    chips.push({ keys: ['platform'], label: PLATFORM_LABELS[f.platform] })
  if (f.clientPlatform !== ALL)
    chips.push({
      keys: ['clientPlatform'],
      label: `Client: ${CLIENT_PLATFORM_LABELS[f.clientPlatform]}`,
    })
  if (f.clientVersion !== ALL)
    chips.push({
      keys: ['clientVersion'],
      label: `Versione ${clientVersionLabel(f.clientVersion)}`,
    })
  if (f.clientVersionBelow.trim())
    chips.push({
      keys: ['clientVersionBelow'],
      label: `Versione < ${f.clientVersionBelow.trim()}`,
    })
  if (f.lastSignInSource !== ALL)
    chips.push({
      keys: ['lastSignInSource'],
      label: `Ultimo login: ${SOURCE_LABELS[f.lastSignInSource]}`,
    })
  if (f.loginMethod !== ALL)
    chips.push({
      keys: ['loginMethod'],
      label: `Login: ${f.loginMethod === 'social' ? 'social' : 'email'}`,
    })
  return chips
}

/** `filters` with the chip's keys back to their initial value. */
export const withoutChip = (f: FilterState, chip: FilterChip): FilterState =>
  chip.keys.reduce((next, key) => ({ ...next, [key]: initialFilters[key] }), f)
